import { Component, Input, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import 'moment/locale/es';
import { Notification } from '../../models/notification.model';
import { NotificationService } from '../../services/notification.service';

@Component({
  selector: 'app-notifications-bell',
  templateUrl: './notifications-bell.component.html',
  styleUrl: './notifications-bell.component.scss'
})
export class NotificationsBellComponent {

  @ViewChild('bellDropdown') private bellDropdown?: NgbDropdown;

  private currentNeighborhoodId?: string;

  /**
   * Fraccionamiento del que se leen los avisos. Es la única puerta de entrada:
   * mientras no llegue, la campana no existe y no se pide nada. Por eso el
   * header del dashboard (que no tiene colonia) no dispara ninguna petición.
   *
   * El contador tiene que estar encendido al entrar, sin abrir el panel, así
   * que la carga vive aquí y no en el `open()` del dropdown. Y se repite en
   * cada cambio de fraccionamiento, después de limpiar el estado anterior.
   */
  @Input()
  set neighborhoodId(value: string | undefined) {

    if (value === this.currentNeighborhoodId) return;

    this.currentNeighborhoodId = value;

    this.notificationService.reset();

    if (!value) return;

    this.notificationService.getAll(value);

  }

  get neighborhoodId(): string | undefined {
    return this.currentNeighborhoodId;
  }

  constructor(
    public notificationService: NotificationService,
    private router: Router
  ) {
    moment.locale('es');
  }

  /* -------------------------------------------------------------------------- */
  /*                               ANCHOR contador                              */
  /* -------------------------------------------------------------------------- */
  // region contador
  public get unseenCount(): number {
    return this.notificationService.unseenCount;
  }

  /** Arriba de 99 el número exacto deja de importar y rompería el círculo. */
  public get badgeLabel(): string {
    return this.unseenCount > 99
      ? '99+'
      : `${this.unseenCount}`;
  }

  /** El color del badge nunca va solo: el botón dice en palabras cuántos hay. */
  public get buttonLabel(): string {

    if (this.notificationService.loading) {
      return 'Avisos, cargando';
    }

    if (this.notificationService.loadFailed) {
      return 'Avisos, no se pudieron cargar';
    }

    if (this.unseenCount === 0) {
      return 'Avisos, ninguno sin ver';
    }

    return this.unseenCount === 1
      ? 'Avisos, 1 sin ver'
      : `Avisos, ${this.unseenCount} sin ver`;

  }

  public get unseenLabel(): string {
    return this.unseenCount === 1
      ? '1 sin ver'
      : `${this.unseenCount} sin ver`;
  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR retry                                */
  /* -------------------------------------------------------------------------- */
  // region retry
  public retry() {

    if (!this.neighborhoodId) return;

    this.notificationService.getAll(this.neighborhoodId);

  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR open                                 */
  /* -------------------------------------------------------------------------- */
  // region open
  /**
   * Abrir un aviso hace dos cosas independientes: marcarlo visto —optimista, sin
   * esperar al servidor, para que el badge baje en el mismo clic— y, si el aviso
   * apunta a un reporte, llevar a la página de reportes pidiéndole que abra ese
   * reporte exacto.
   *
   * El aviso trae el `reportId` pero no sabe en qué bandeja vive el reporte, así
   * que se entra por la bandeja por defecto y es `reports-page` —que sí tiene las
   * tres listas cargadas— quien corrige la bandeja antes de abrir el modal. Se
   * entra por `pendings` explícito y no por `/reports` a secas para no depender
   * de que el `redirectTo` conserve el query param.
   */
  public async open(
    notification: Notification
  ) {

    this.bellDropdown?.close();

    if (this.neighborhoodId) {
      this.notificationService.markAsSeen(
        this.neighborhoodId,
        notification
      );
    }

    if (!notification.isReport || !this.neighborhoodId) return;

    await this.router.navigate(
      [
        '/neighborhoods',
        this.neighborhoodId,
        'reports',
        'pendings'
      ],
      {
        queryParams: {
          reportId: notification.reportId
        }
      }
    );

  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR icon                                 */
  /* -------------------------------------------------------------------------- */
  // region icon
  /** Icono del aviso. Se decide por lo que el aviso puede abrir, no por el
      catálogo de tipos: el `code` del tipo no está garantizado aquí. */
  public icon(
    notification: Notification
  ): string {
    return notification.isReport
      ? 'report_problem'
      : 'notifications';
  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR dates                                */
  /* -------------------------------------------------------------------------- */
  // region dates
  public relativeDateLabel(
    notification: Notification
  ): string {
    return moment(notification.createdAt).fromNow();
  }

  public dateLabel(
    notification: Notification
  ): string {
    return moment(notification.createdAt).format('DD MMM YYYY, HH:mm');
  }

  public dateTime(
    notification: Notification
  ): string {
    return moment(notification.createdAt).toISOString();
  }

}
